import imageCompression from "browser-image-compression";
import { supabase } from "@/integrations/supabase/client";

// ============================================================
// Meia-entrada — upload e visualização de documento comprobatório
// Bucket privado `half-price-docs`, path {event_id}/{ticket_id}/{arquivo}.
// Acesso controlado por RLS (comprador dono, produtor do evento, admin).
// ============================================================

export const HALF_PRICE_BUCKET = "half-price-docs";

// Formatos aceitos (spec 3.2)
export const HALF_PRICE_ACCEPTED_MIME = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
  "image/heic",
  "image/heif",
  "application/pdf",
];

export const HALF_PRICE_ACCEPT_ATTR = ".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif";

// Tamanho máximo antes da compactação: 5 MB
export const HALF_PRICE_MAX_BYTES = 5 * 1024 * 1024;

export interface HalfPriceDocument {
  id: string;
  ticket_id: string;
  event_id: string;
  storage_path: string;
  file_name: string;
  file_size_bytes: number;
  mime_type: string;
  uploaded_at: string;
}

function fileExtension(file: File): string {
  const fromName = file.name.split(".").pop()?.toLowerCase();
  if (fromName && fromName.length <= 5) return fromName;
  const fromType = file.type.split("/").pop()?.toLowerCase();
  return fromType || "bin";
}

export function isAcceptedHalfPriceFile(file: File): boolean {
  if (HALF_PRICE_ACCEPTED_MIME.includes(file.type)) return true;
  // Alguns navegadores não preenchem o mime de HEIC; cai para a extensão
  const ext = fileExtension(file);
  return ["pdf", "png", "jpg", "jpeg", "webp", "heic", "heif"].includes(ext);
}

/**
 * Compacta imagens client-side (alvo ≤ 2 MB). PDFs não são comprimidos (D5).
 * Em caso de falha (ex.: HEIC sem suporte de canvas), retorna o arquivo original.
 */
export async function compressHalfPriceFile(file: File): Promise<File> {
  if (file.type === "application/pdf" || fileExtension(file) === "pdf") {
    return file;
  }
  try {
    const compressed: Blob = await imageCompression(file, {
      maxSizeMB: 2,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
    });
    // browser-image-compression pode retornar Blob; normaliza para File
    if (compressed instanceof File) return compressed;
    return new File([compressed], file.name, { type: compressed.type || file.type });
  } catch (err) {
    console.warn("[half-price] compressão falhou, usando arquivo original:", err);
    return file;
  }
}

/**
 * Faz upload do documento de meia-entrada de um ingresso e registra na tabela.
 * Re-upload sobrescreve o documento anterior (D2).
 */
export async function uploadHalfPriceDocument(params: {
  ticketId: string;
  eventId: string;
  file: File;
}): Promise<HalfPriceDocument> {
  const { ticketId, eventId, file } = params;

  if (!isAcceptedHalfPriceFile(file)) {
    throw new Error("Formato não aceito. Envie PDF, PNG, JPG, WEBP ou HEIC.");
  }
  if (file.size > HALF_PRICE_MAX_BYTES) {
    throw new Error("Arquivo muito grande. O tamanho máximo é 5 MB.");
  }

  const finalFile = await compressHalfPriceFile(file);

  // Remove documento anterior (objeto + permite que o upsert da linha sobrescreva)
  const { data: existing } = await supabase
    .from("half_price_documents")
    .select("storage_path")
    .eq("ticket_id", ticketId)
    .maybeSingle();
  if (existing?.storage_path) {
    await supabase.storage.from(HALF_PRICE_BUCKET).remove([existing.storage_path]);
  }

  const ext = fileExtension(finalFile);
  const storagePath = `${eventId}/${ticketId}/${crypto.randomUUID()}.${ext}`;

  const { error: uploadErr } = await supabase.storage
    .from(HALF_PRICE_BUCKET)
    .upload(storagePath, finalFile, {
      contentType: finalFile.type || file.type || "application/octet-stream",
      upsert: true,
    });
  if (uploadErr) throw uploadErr;

  const { data: userData } = await supabase.auth.getUser();

  const row = {
    ticket_id: ticketId,
    event_id: eventId,
    storage_path: storagePath,
    file_name: file.name,
    file_size_bytes: finalFile.size,
    mime_type: finalFile.type || file.type || "application/octet-stream",
    uploaded_by: userData?.user?.id ?? null,
    uploaded_at: new Date().toISOString(),
  };

  const { data, error } = await supabase
    .from("half_price_documents")
    .upsert(row, { onConflict: "ticket_id" })
    .select()
    .single();
  if (error) {
    // rollback do objeto se o registro falhar
    await supabase.storage.from(HALF_PRICE_BUCKET).remove([storagePath]).catch(() => {});
    throw error;
  }
  return data as HalfPriceDocument;
}

/** Gera uma signed URL temporária (TTL padrão 60 min) para visualização. */
export async function getHalfPriceDocumentSignedUrl(
  storagePath: string,
  expiresIn = 3600
): Promise<string> {
  const { data, error } = await supabase.storage
    .from(HALF_PRICE_BUCKET)
    .createSignedUrl(storagePath, expiresIn);
  if (error || !data?.signedUrl) {
    throw error || new Error("Não foi possível gerar o link do documento.");
  }
  return data.signedUrl;
}

/** Retorna os documentos de um conjunto de ingressos, indexados por ticket_id. */
export async function getHalfPriceDocumentsByTickets(
  ticketIds: string[]
): Promise<Record<string, HalfPriceDocument>> {
  if (ticketIds.length === 0) return {};
  const { data, error } = await supabase
    .from("half_price_documents")
    .select("*")
    .in("ticket_id", ticketIds);
  if (error) throw error;
  const map: Record<string, HalfPriceDocument> = {};
  for (const doc of (data || []) as HalfPriceDocument[]) {
    map[doc.ticket_id] = doc;
  }
  return map;
}
